import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from "react"
import {
  Box, Flex, Text, Tooltip,
} from "@chakra-ui/react"
import EventGroupItem from "components/elements/EventGroupItem"
import { EventGroup } from "sharedTypes"

type TagItemProps = {
  eventGroup: EventGroup;
  shrink: boolean;
}

const isTruncated = (el: Element): boolean => {
  if (el.scrollWidth > el.clientWidth) return true

  return Array.from(el.children).some(isTruncated)
}

const TagItem = ({ eventGroup, shrink }: TagItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <Tooltip label={eventGroup.title} isOpen={showTooltip}>
      <Box
        ref={ref}
        onMouseEnter={() => ref.current && setShowTooltip(isTruncated(ref.current))}
        onMouseLeave={() => setShowTooltip(false)}
        overflow="hidden"
        flexShrink={shrink ? 1 : 0}
        minW={shrink ? 0 : undefined}
      >
        <EventGroupItem
          icon={eventGroup.icon}
          color={eventGroup.color}
          title={eventGroup.title}
          compact
        />
      </Box>
    </Tooltip>
  )
}

type Props = {
  eventGroups: EventGroup[];
}

const TagsCell = ({ eventGroups }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [maxVisible, setMaxVisible] = useState(eventGroups.length)

  // When column gets wider, debounce the reset so we don't cascade during drag.
  // Also resets maxVisible when tag count changes (e.g. row reuse in virtualized list).
  useEffect(() => {
    const el = containerRef.current
    if (!el) return undefined

    setMaxVisible(eventGroups.length)

    let timer: ReturnType<typeof setTimeout>
    let prevWidth = Math.round(el.getBoundingClientRect().width)

    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0)
      if (width > prevWidth) {
        clearTimeout(timer)
        timer = setTimeout(() => setMaxVisible(eventGroups.length), 150)
      }
      prevWidth = width
    })
    observer.observe(el)

    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [eventGroups.length])

  // After layout, if content overflows horizontally, show one fewer tag.
  // Stops at 1 — the single remaining tag truncates via CSS.
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || container.clientWidth === 0 || maxVisible <= 1) return

    if (container.scrollWidth > container.clientWidth + 2) {
      setMaxVisible((v) => v - 1)
    }
  })

  if (eventGroups.length === 0) return null

  const effectiveMax = Math.max(1, Math.min(maxVisible, eventGroups.length))
  const hiddenCount = eventGroups.length - effectiveMax
  const isSingleVisible = effectiveMax === 1

  return (
    <Flex ref={containerRef} gap={2} overflow="hidden" align="center" width="100%">
      {eventGroups.slice(0, effectiveMax).map((eventGroup, index) => (
        <TagItem
          key={eventGroup.id || index}
          eventGroup={eventGroup}
          shrink={isSingleVisible}
        />
      ))}
      {hiddenCount > 0 && (
        <Tooltip
          label={eventGroups.slice(effectiveMax).map((g) => g.title).join(", ")}
        >
          <Text as="span" fontSize="sm" whiteSpace="nowrap" flexShrink={0}>
            +{hiddenCount} more
          </Text>
        </Tooltip>
      )}
    </Flex>
  )
}

export default TagsCell
